import { useMemo } from 'react';
import { t } from '@lingui/macro';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { siteFilterId, timeRangeFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Site Channel Utilization (Avg)`;

const dataSource = {
    url: '/ngp/v2/sites/perf',
    params: {
        metrics: ['siteChannelUtilization24G', 'siteChannelUtilization5G', 'siteChannelUtilizationBoth'],
    },
};

const BAND_24G = '24G';
const BAND_50G = '5G';
const BAND_BOTH = 'Both';

const seriesLabel = {
    [BAND_24G]: t`Average 2.4 GHz Channel Utilization`,
    [BAND_50G]: t`Average 5 GHz Channel Utilization`,
    [BAND_BOTH]: t`Average 2.4/5 GHz Channel Utilization`,
};

function useChartConfig(type) {
    const color = useMetricColor('networkIn');
    const key = `siteChannelUtilization${type}`;
    const label = seriesLabel[type];

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Utilization (%)`,
                    },
                    ceiling: 100,
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.PERCENT,
                    units: '%',
                    chartType: 'spline',
                    color,
                    label,
                    key,
                    value: 'avg',
                    multiplier: 1,
                    forceDecimals: true,
                },
            ],
        }),
        [key, label, color]
    );
}

const selections = [
    { value: BAND_24G, label: t`2.4 GHz` },
    { value: BAND_50G, label: t`5 GHz` },
    { value: BAND_BOTH, label: t`Both` },
];

/**
 * NOTE: this is a Site panel, but does not appear on the Site Detail page - it's a custom dashboard
 * panel created primarily for use by USAA, but is in the panel catalog for anyone else who wants it.
 *
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
function SiteRadioChannelUtilizationPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, BAND_24G);
    const data = usePanelDataService(dataSource);
    const chartConfig = useChartConfig(chartDisplayType);
    const chartData = data?.data?.[0] || [];

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

SiteRadioChannelUtilizationPanel.propTypes = panelPropTypes;

SiteRadioChannelUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    minWidth: 4,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'SiteRadioChannelUtilizationTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.SITE,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Channel utilization trend for a selected site and wireless radio band`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, siteFilterId],
        optional: [],
    },
})(SiteRadioChannelUtilizationPanel);
